import "./airdrop.scss";
import { useWeb3Context } from "../../hooks";
import { useDispatch, useSelector } from "react-redux";
import { IReduxState } from "src/store/slices/state.interface";
import { claim } from "src/store/slices/airdrop-thunk";
import { useState, useCallback, useEffect } from "react";
import { Grid, InputAdornment, OutlinedInput, Zoom, Box, Modal, withStyles, Tooltip, Divider } from "@material-ui/core";
import { formatValue, formatValueDigits, trim } from "../../helpers";
import { IPendingTxn, isPendingTxn, txnButtonText } from "../../store/slices/pending-txns-slice";
import { Skeleton } from "@material-ui/lab";
import { ethers } from "ethers";
import { getAddresses, Networks } from "src/constants";
import { AirdropContract, MasterMantisContract, MntVerifyContract, StableReserveContract } from "src/abi";
import { fetchAccountSuccess } from "src/store/slices/account-slice";
import InfoIcon from "../../assets/icons/info-solid.svg";
import ExternalIcon from "../../assets/icons/external-link.svg";
import CheckIcon from "../../assets/icons/check-circle.svg";
import { apiGetRequest, apiPostRequest } from "src/helpers/api-request";

const BASE_URL = "https://api.mantissa.finance/api/airdrop/";
// const BASE_URL = "http://localhost:8000/api/airdrop/";

function Airdrop() {
    const dispatch = useDispatch();
    const { provider, address, connect, chainID, checkWrongNetwork } = useWeb3Context();
    const addresses = getAddresses(chainID);

    const [protocolPoints, setProtocolPoints] = useState<number>(0);
    const [referralPoints, setReferralPoints] = useState<number>(0);

    const [referralCode, setReferralCode] = useState<string>("");
    const [referralDisabled, setReferralDisabled] = useState<boolean>(false);

    const [rank, setRank] = useState<string>("-");
    const [myCode, setMyCode] = useState<string>("");
    const [usersReferred, setUsersReferred] = useState<number>(0);

    const isAppLoading = useSelector<IReduxState, boolean>(state => state.app.loading);

    const LightTooltip = withStyles(theme => ({
        arrow: {
            color: "rgba(11, 0, 30, 1)",
        },
        tooltip: {
            backgroundColor: "rgba(11, 0, 30, 1)",
            fontSize: 11,
            fontStyle: "Proxima",
        },
    }))(Tooltip);

    const applyInviteCode = async () => {
        if (address && !referralDisabled) {
            try {
                const url = BASE_URL + "invite/";
                const response = await apiPostRequest(url, {
                    invite_code: referralCode,
                    wallet: address,
                });
                if (response["success"]) {
                    setReferralDisabled(true);
                } else {
                    setReferralCode("");
                }
            } catch {
                setReferralCode("");
            }
        }
    };

    async function getPointsData() {
        const invite_url = BASE_URL + "invite/?wallet=" + address;
        const invite_response = await apiGetRequest(invite_url);
        const invite_code = invite_response["invite_code"];
        if (invite_code) {
            setReferralDisabled(true);
            setReferralCode(invite_code);
        }

        const points_url = BASE_URL + "points/?wallet=" + address + "&chain=" + chainID;
        const points_response = await apiGetRequest(points_url);
        const total_normal_points = points_response["total_normal_points"];
        const total_referee_points = points_response["total_referee_points"];
        const total_referral_points = points_response["total_referral_points"];

        setProtocolPoints(total_normal_points + total_referee_points);
        setReferralPoints(total_referral_points);
        setRank(points_response["rank"]);
        setMyCode(points_response["my_code"]);
        setUsersReferred(points_response["my_referred"]);
    }

    useEffect(() => {
        getPointsData();
    }, []);

    return (
        <div className="airdrop-view">
            <Zoom in={true}>
                <div className="airdrop-card mb-3">
                    <Grid className="airdrop-card-grid" container direction="column" spacing={2}>
                        <Grid item>
                            <div className="airdrop-card-header">
                                <p className="airdrop-card-header-title">Airdrop</p>
                            </div>
                        </Grid>
                        <div className="airdrop-link-box">
                            <p>To be eligible for Airdrop program, please complete the following tasks as part of our social onboarding:</p>
                            <p>
                                - Follow us on X{" "}
                                <a href="https://x.com/MantisSwap" target="_blank">
                                    <img src={ExternalIcon} width={20} style={{ marginTop: "10px" }} />
                                </a>
                            </p>
                            <p>
                                - Join our Discord server{" "}
                                <a href="https://discord.com/invite/pghwwJXCTN" target="_blank">
                                    <img src={ExternalIcon} width={20} style={{ marginTop: "10px" }} />
                                </a>
                            </p>
                        </div>

                        <div className="airdrop-card-area">
                            {!address && (
                                <div className="airdrop-card-wallet-notification">
                                    <div className="airdrop-card-wallet-connect-btn" onClick={connect}>
                                        <p>Connect Wallet</p>
                                    </div>
                                    <p className="airdrop-card-wallet-desc-text">Connect your wallet!</p>
                                </div>
                            )}
                            {address && (
                                <div className="airdrop-card-action-area">
                                    <p className="airdrop-card-action-heading">
                                        Got a friend's referral code?{"  "}
                                        <LightTooltip title="Earn 5% boost on your points by entering a friend's referral code" placement="top-start" arrow>
                                            <img className="tooltip-icon" alt="" src={InfoIcon} width={14} height={14} />
                                        </LightTooltip>
                                    </p>
                                    <div className="airdrop-card-action-row">
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <OutlinedInput
                                                    type="text"
                                                    placeholder="Enter Referral Code"
                                                    className="airdrop-card-action-input"
                                                    value={referralCode}
                                                    onChange={e => setReferralCode(e.target.value)}
                                                    labelWidth={0}
                                                    disabled={referralDisabled}
                                                    endAdornment={
                                                        referralDisabled && (
                                                            <InputAdornment position="end">
                                                                <img src={CheckIcon} width={16} />
                                                            </InputAdornment>
                                                        )
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <div className="airdrop-card-tab-panel">
                                                    <div
                                                        className="airdrop-card-tab-panel-btn"
                                                        onClick={() => {
                                                            applyInviteCode();
                                                        }}
                                                    >
                                                        <p>Apply</p>
                                                    </div>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </div>
                                    {myCode && (
                                        <>
                                            <hr color="#675D95" style={{ marginTop: "16px", marginBottom: "16px" }} />
                                            <p className="airdrop-card-action-heading">My Referrals</p>
                                            <div className="airdrop-card-action-row">
                                                <p className="title">
                                                    Unique Code{"  "}
                                                    <LightTooltip title="Earn 10% of the points earned by each user you refer" placement="top-start" arrow>
                                                        <img className="tooltip-icon" alt="" src={InfoIcon} width={14} height={14} />
                                                    </LightTooltip>
                                                </p>
                                                <p className="value">{myCode}</p>
                                            </div>
                                            <div className="airdrop-card-action-row">
                                                <p className="title">Users Referred</p>
                                                <p className="value">{usersReferred}</p>
                                            </div>
                                            <hr color="#675D95" style={{ marginTop: "16px" }} />
                                        </>
                                    )}
                                    <p className="airdrop-card-action-heading" style={{ marginTop: "16px" }}>
                                        My Points
                                    </p>
                                    <div className="airdrop-user-data">
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <div className="data-row">
                                                    <div className="data-row-title">
                                                        <p className="data-row-name">Total Points</p>
                                                    </div>
                                                    <p className="data-row-value">{formatValueDigits((protocolPoints + referralPoints).toString(), 0)}</p>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <div className="data-row">
                                                    <div className="data-row-title">
                                                        <p className="data-row-name">Rank</p>
                                                    </div>
                                                    <p className="data-row-value">{formatValueDigits(rank.toString(), 0)}</p>
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <div className="data-row">
                                                    <div className="data-row-title">
                                                        <p className="data-row-name">Protocol Points</p>
                                                    </div>
                                                    <p className="data-row-value">{formatValueDigits(protocolPoints.toString(), 0)}</p>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <div className="data-row">
                                                    <div className="data-row-title">
                                                        <p className="data-row-name">Referral Points</p>
                                                    </div>
                                                    <p className="data-row-value">{formatValueDigits(referralPoints.toString(), 0)}</p>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Grid>
                </div>
            </Zoom>
        </div>
    );
}
export default Airdrop;
